import { type Key, useState } from 'react';
import { BiComment, BiDislike, BiLike, BiShow, BiTrash } from 'react-icons/bi';
import { BsX } from 'react-icons/bs';
import { MdOutlineHideSource } from 'react-icons/md';
import { iconClasses } from '@/config/job';
import { ActionDropdown, Button, Confirm, cn } from '@/core/ui';

type CommentMultiAction = {
  key: string;
  title: string;
  operation: string;
  type: 'success' | 'danger' | 'warn' | 'info' | 'default';
  icon: React.ComponentType<{ className?: string }>;
  color?: 'default' | 'danger' | 'warning' | 'secondary';
};

const COMMENT_MULTI_ACTIONS: CommentMultiAction[] = [
  {
    key: 'delete',
    title: 'Delete',
    operation: 'delete',
    type: 'danger',
    icon: BiTrash,
    color: 'danger',
  },
  {
    key: 'hide',
    title: 'Hide',
    operation: 'hide',
    type: 'warn',
    icon: MdOutlineHideSource,
    color: 'warning',
  },
  {
    key: 'unhide',
    title: 'Unhide',
    operation: 'unhide',
    type: 'warn',
    icon: BiShow,
    color: 'secondary',
  },
  {
    key: 'resetLikes',
    title: 'Reset Likes',
    operation: 'resetLikes',
    type: 'default',
    icon: BiLike,
  },
  {
    key: 'resetDislikes',
    title: 'Reset Dislikes',
    operation: 'resetDislikes',
    type: 'default',
    icon: BiDislike,
  },
  {
    key: 'review',
    title: 'Review Comment',
    operation: 'review',
    type: 'default',
    icon: BiComment,
  },
  {
    key: 'unreview',
    title: 'Unreview Comment',
    operation: 'unreview',
    type: 'default',
    icon: BiComment,
  },
];

export const CommentMultiActions = ({
  selectedComments,
  onAction,
  isLoading,
  onClearSelection,
}: {
  selectedComments: (string | number)[];
  onAction: (ids: (string | number)[], operation: string) => void;
  isLoading: boolean;
  onClearSelection: () => void;
}) => {
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  if (!selectedComments.length) {
    return null;
  }

  const actionItems = COMMENT_MULTI_ACTIONS.map((action) => ({
    key: action.key,
    className: 'text-xs py-1',
    startContent: (
      <action.icon
        className={cn(iconClasses, {
          'text-danger-300': action.color === 'danger',
          'text-warning-500': action.color === 'warning',
          'text-secondary-500': action.color === 'secondary',
        })}
      />
    ),
    title: action.title,
    color: action.color,
  }));

  const handleAction = (key: Key) => {
    const action = COMMENT_MULTI_ACTIONS.find((a) => a.key === key);
    if (action) {
      setSelectedAction(action.operation);
      setIsConfirmOpen(true);
    }
  };

  const handleConfirm = () => {
    if (selectedAction) {
      onAction(selectedComments, selectedAction);
      setIsConfirmOpen(false);
      setSelectedAction(null);
    }
  };

  const currentAction = selectedAction
    ? COMMENT_MULTI_ACTIONS.find((a) => a.operation === selectedAction)
    : null;

  const getDescription = (operation: string, count: number) => {
    switch (operation) {
      case 'delete':
        return `Are you sure you want to delete ${count} ${count === 1 ? 'comment' : 'comments'}?`;
      case 'hide':
        return `Are you sure you want to hide ${count} ${count === 1 ? 'comment' : 'comments'}?`;
      case 'unhide':
        return `Are you sure you want to unhide ${count} ${count === 1 ? 'comment' : 'comments'}?`;
      case 'resetLikes':
        return `Are you sure you want to reset likes for ${count} ${count === 1 ? 'comment' : 'comments'}?`;
      case 'resetDislikes':
        return `Are you sure you want to reset dislikes for ${count} ${count === 1 ? 'comment' : 'comments'}?`;
      case 'review':
        return `Are you sure you want to review ${count} ${count === 1 ? 'comment' : 'comments'}?`;
      case 'unreview':
        return `Are you sure you want to unreview ${count} ${count === 1 ? 'comment' : 'comments'}?`;
      default:
        return `Are you sure you want to perform this action on ${count} ${count === 1 ? 'comment' : 'comments'}?`;
    }
  };

  return (
    <>
      <div className='flex items-center gap-2'>
        <Button
          size='sm'
          variant='flat'
          className='flex items-center px-0'
          onPress={onClearSelection}
          aria-label='Clear selected comments'
        >
          <span className='text-xs flex items-center justify-center text-default-900 rounded-md'>
            {selectedComments.length}
          </span>
          <BsX className='size-4' />
        </Button>

        <ActionDropdown
          offset={0}
          classNames={{
            content: 'shadow-sm border border-default-200 rounded-md min-w-[130px]',
          }}
          onAction={handleAction}
          items={actionItems}
        >
          <Button size='sm' variant='flat' color='default'>
            Actions
          </Button>
        </ActionDropdown>
      </div>

      {currentAction && (
        <Confirm
          type={currentAction.type}
          title={`${currentAction.title} Comments`}
          isLoading={isLoading}
          isOpen={isConfirmOpen}
          onOpenChange={setIsConfirmOpen}
          message={
            <div className='flex flex-col gap-2'>
              <p>{getDescription(currentAction.operation, selectedComments.length)}</p>
              <div className='bg-warning-50 border border-warning-200 rounded p-2 mt-2'>
                <p className='text-xs text-warning-700 font-semibold mb-1'>Selected Comment IDs:</p>
                <ul className='list-disc list-inside text-xs text-warning-800 max-h-32 overflow-y-auto'>
                  {selectedComments.map((id) => (
                    <li key={id}>{id}</li>
                  ))}
                </ul>
              </div>
            </div>
          }
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
};
